/* file description */

import type { RelationTreeType } from '@/asset/AssetTask/templates/Cascade/type';
import useMetaStore from '@/store/useMetaStore';
import useValuesStore from '@/store/useValuesStore';
import { Api as FetchAPI } from '@/utils';
import { useMemo, useState } from 'react';
import type { MetaOfRule } from '@/store/type';
import { ruleMapMatchExec, getValuesByRow } from '@/store/rulesCalc';
import { getJsonAta } from '@/utils/jsonataFactory';

interface GetSPTableWithModelSchemaProps {
  schemaList: RelationTreeType[] | never[];
  metaInfoByRule?: MetaOfRule;
  rowId: string | undefined;
}
export type useSPTableWithModelSchemaReturnType = ReturnType<
  typeof useSPTableWithModelSchema
>;
// 获取recordListModule对应schema
export const useSPTableWithModelSchema = ({
  schemaList,
  rowId,
  metaInfoByRule,
}: GetSPTableWithModelSchemaProps) => {
  // 缓存选中的行
  const [selectedRows, setSelectedRows] = useState<Record<string, any>>({});

  const sotreState = useMetaStore();
  const { values, setValues, rulesMap } = useValuesStore();

  const spRecordListSchema = useMemo(() => {
    const { moduleList, moduleTaskField, pageConfig, pageType, utils, myRoles } = sotreState;


    return schemaList?.map((schemaRelation, i: number) => {
      const pageModuleConfig = pageConfig?.pageModuleConfig || {};
      const { parentCode, parentAttrCode } = schemaRelation;
      const resultModule = moduleList.find(
        (item) => item.handle === schemaRelation?.code,
      );
      if (!resultModule) return;

      const {
        name: moduleName,
        handle: moduleHandle,
        namespaceHandle,
      } = resultModule;

      if (pageModuleConfig?.[schemaRelation?.code]?.tabShow === false) return;

      // 生成colums
      // const columns = resultModule?.fields;
      let columns = resultModule?.fields.map((field: Record<string, any>) => {
        const f = moduleTaskField[moduleHandle]?.find(
          (item) => item.field_id === field.fieldID,
        );

        const configList = pageModuleConfig[moduleHandle]?.configList;
        // 是否搜索
        const search = configList?.find(
          (item) => item.fieldID === field.fieldID,
        )?.showSearch || false;
        // 搜索默认值
        const searchDefaultValue = configList?.find(
          (item) => item.fieldID === field.fieldID,
        )?.searchOptions?.defaultValue;
        // 展示配置-宽度
        const width = configList?.find(
          (item) => item.fieldID === field.fieldID,
        )?.showOptions?.width;

        const columsOption = {
          dataIndex: field.name,
          fieldID: field.fieldID,
          isRequired: !!f?.required,
          search,
          defaultValue: searchDefaultValue,
          width,
        };
        // 生成或者引用
        if (f?.field_from === 'quote') {
          return {
            ...columsOption,
            isDisabled: true,
          };
        }
        return {
          ...columsOption,
        };
      });

      if (pageModuleConfig?.[moduleHandle]) {
        const moduleFieldsConfigList = pageModuleConfig[moduleHandle].configList;
        const existFieldIDs = moduleFieldsConfigList
          .filter((field: Record<string, any>) => field.showSource)
          .map((field: Record<string, any>) => field.fieldID);
        columns = columns.filter((field: Record<string, any>) =>
          existFieldIDs.includes(field.fieldID));
        // 排序
        columns.sort(
          (prevField: Record<string, any>, nextField: Record<string, any>) => {
            const prevFieldSort = moduleFieldsConfigList.findIndex(
              (field: Record<string, any>) => {
                return field.fieldID === prevField.fieldID;
              },
            );
            const nextFieldSort = moduleFieldsConfigList.findIndex(
              (field: Record<string, any>) => {
                return field.fieldID === nextField.fieldID;
              },
            );
            return prevFieldSort - nextFieldSort;
          },
        );
      }

      // 获取上传模型映射
      const fileFieldMap =
        pageModuleConfig?.[moduleHandle]?.configList
          ?.map((item) => {
            if (item?.relationName) {
              return { name: item.name, value: item.relationName };
            }
          })
          .filter((item) => item !== undefined) || [];

      // 基于规则生成的meta信息处理colums
      const ruleFields = metaInfoByRule?.[moduleHandle]?.fields || [];
      ruleFields.forEach((ruleField) => {
        const column = columns.find(
          (col: any) => col.dataIndex === ruleField.dataIndex,
        );
        if (column) {
          Object.assign(column, ruleField);
        }
      });

      // 每行的规则处理
      columns?.forEach((field) => {
        field.getRowConfig = (record: any) => {
          const res = ruleMapMatchExec(
            rulesMap,
            [moduleHandle, field.dataIndex].join('.'),
            getValuesByRow(moduleHandle, record, values, sotreState?.taskInfo?.relationTree),
            myRoles,
          );
          return res;
        };
      });
      // console.log(selectedRows, 'selectedRows');

      return {
        componentName: 'Tabs.TabPane',
        sequence: pageModuleConfig?.[moduleHandle]?.sequence || 0,
        props: {
          tab: pageModuleConfig?.[moduleHandle]?.tabName || moduleName,
          key: `tab-item-${i}`,
          forceRender: true,
        },
        hidden: false,
        isLocked: false,
        condition: true,
        conditionGroup: '',
        handle: moduleHandle,
        children: [
          {
            componentName: 'SPtable',
            props: {
              ...pageModuleConfig?.[moduleHandle],
              API: { FetchAPI },
              title: pageModuleConfig?.[moduleHandle]?.tabName || moduleName,
              search: columns?.filter((item) => item.search).length !== 0,
              copyable: false,
              options: false,
              rowDelable: !pageModuleConfig?.[moduleHandle]?.hideRowDelete,
              autoRowEditConfig: {
                hideSave: false,
              },
              headerTitle: 'none',
              moduleID: moduleHandle,
              namespaceID: namespaceHandle,
              assetModuleInfo: resultModule,
              columns,
              tableBaseMode: pageType,
              dataVersion: utils?.queryParams?.version || '0',
              hidePagination: pageModuleConfig?.[moduleHandle]?.hidePagination,
              fileConfig: {
                fileAutoSave:
                  pageModuleConfig?.[moduleHandle]?.isFileModule &&
                  pageType === 'view',
                fileModule: pageModuleConfig?.[moduleHandle]?.isFileModule,
                mainModuleData: {
                  [parentAttrCode]: values?.[parentCode]?.[parentAttrCode] || '',
                },
                fileFieldMap,
                fileData: {
                  _headId: rowId,
                },
              },
              selectedRows: selectedRows[moduleHandle] ?? [],
              rowSelection:
                pageModuleConfig?.[moduleHandle]?.selectType ||
                  pageModuleConfig?.[moduleHandle]?.selectType === 'none'
                  ? {
                    type: pageModuleConfig?.[moduleHandle]?.selectType,
                    onChange: (
                      selectedRowKeys: React.Key[],
                      selectedRows: any[],
                    ) => {
                      setSelectedRows({ [moduleHandle]: selectedRows });
                    },
                    getCheckboxProps: (record: any) => {
                      const rowSelectionDisabled = pageModuleConfig?.[moduleHandle]?.rowSelectionDisabled;
                      if (rowSelectionDisabled) {
                        const exp = getJsonAta(rowSelectionDisabled);
                        const checkboxProps = { disabled: false };
                        try {
                          checkboxProps.disabled = exp.evaluate({ [moduleHandle]: record });
                        } catch (e) {
                          console.warn(e);
                        }
                        return checkboxProps;
                      }
                      return {};
                    },
                  }
                  : undefined,
              footerExpression: pageModuleConfig?.[moduleHandle]?.footerExpression,
              mobileRows: pageModuleConfig?.[moduleHandle]?.mobileFieldShow,
            },
            hidden: false,
            isLocked: false,
            condition: true,
            conditionGroup: '',
          },
        ],
      };
    });
  }, [schemaList, sotreState, values, metaInfoByRule, rowId, selectedRows, rulesMap]) || [];

  return {
    spRecordListSchema: spRecordListSchema
      .filter((item) => item !== undefined)
      .sort((pre, next) => pre.sequence - next.sequence),
    selectedRows,
    setSelectedRows,
  } as const;
};
